/**
 * Admin — one hub for the leadership-only setup pages (Oct 12, 2026).
 *
 * Replaces three separate nav entries (Team Roster, Team Boards, Hover) with
 * one, and gives the QuickBooks connect button a home again: it was mounted
 * on an /admin route that no longer existed, so the Intuit OAuth callback
 * (which redirects to /admin?qb_oauth=…) landed on a 404.
 *
 * Oct 19, 2026: finance (Shannon) opens this hub too, for the Team Roster,
 * which she edits like leadership (lib/adminAccess.ts). Everything else stays
 * leadership-only: the Team Boards and Hover routes refuse finance, and the
 * QuickBooks / Google Calendar cards can reconnect a whole integration.
 */
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { isLeadership } from "../lib/adminAccess";
import QuickBooksConnect from "../components/admin/QuickBooksConnect";
import GoogleCalendarConnect from "../components/admin/GoogleCalendarConnect";
import BooksTradeOrders from "../components/admin/BooksTradeOrders";

const LINKS: Array<{ to: string; icon: string; title: string; blurb: string; leadershipOnly?: boolean }> = [
  { to: "/team-roster", icon: "👥", title: "Team Roster", blurb: "Add, edit, or offboard team members. Commission rates live here." },
  { to: "/team-boards", icon: "🎛", title: "Team Boards", blurb: "See and edit anyone's home widget board.", leadershipOnly: true },
  { to: "/integrations/hover", icon: "🏠", title: "Hover", blurb: "Connection status, webhook health, and recent measurement jobs.", leadershipOnly: true },
  { to: "/phone-health", icon: "📞", title: "Phone Health", blurb: "Calls answered and missed, who it rang, failed texts, Twilio errors, and line routing.", leadershipOnly: true },
];

export default function AdminHub() {
  const { role, isSuperUser } = useAuth();
  const leader = isLeadership(role, isSuperUser);
  const links = LINKS.filter((l) => leader || !l.leadershipOnly);

  return (
    <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
      <div>
        <h1 className="text-lg font-bold text-slate-900">Admin</h1>
        <p className="text-sm text-slate-500">
          {leader ? "Setup and leadership tools. Nothing here is day-to-day." : "Team setup. Nothing here is day-to-day."}
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {links.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className="rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-300 transition"
          >
            <div className="text-xl">{l.icon}</div>
            <div className="mt-1 text-sm font-bold text-slate-900">{l.title}</div>
            <div className="mt-0.5 text-xs text-slate-500">{l.blurb}</div>
          </Link>
        ))}
      </div>
      {leader && (
        <>
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="text-sm font-bold text-slate-900 mb-2">QuickBooks Online</h2>
            <QuickBooksConnect />
          </section>
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="text-sm font-bold text-slate-900 mb-2">Google Calendar — Team Alex Builds</h2>
            <GoogleCalendarConnect />
            {/* Oct 29, 2026: the JAC sales orders in Books (Other Trades' jobs), turned on here */}
            <BooksTradeOrders />
          </section>
        </>
      )}
    </div>
  );
}
